import {defineMessage, defineMessages} from 'react-intl'

import {FilterId} from '../../search/types'
import {clientOnly} from '../capabilities/clientOnly'
import {CapabilityContext} from '../capabilities/types/core'
import {RetentionCapabilityContext} from '../capabilities/types/domains'
import {TIER_LABELS} from '../constants'
import {Brand, BrandCode} from '../types'
import {TopDealsDestination} from './topDealsDestinations'

interface BaseConfig
  extends Omit<
    Brand,
    | 'code'
    | 'name'
    | 'providerCode'
    | 'level'
    | 'theme'
    | 'links'
    | 'isLive'
    | 'siteAddresses'
  > {}

const baseConfig: BaseConfig = {
  tagline: 'Find hidden hotel deals',
  metaDescription: 'Find hidden hotel deals',
  messages: defineMessages({
    pageTitleMessage: {
      id: 'title.legacyPageTitle',
      defaultMessage: 'Find the right hotel at the best price.'
    },
    pageTitleTagLineMessage: {
      id: 'title.legacyPageTitle',
      defaultMessage: 'Find the right hotel at the best price.'
    },
    homepageHeadingMessage: {
      id: 'homePage.headingDefault',
      defaultMessage: 'Find hidden hotel deals'
    },
    taxesDisclaimerMessage: {
      id: 'taxesAndFeesPopupHeaderText.generic',
      defaultMessage:
        'Taxes and fees are generally recovery charges paid to partners (e.g. hotels).'
    },
    merchantPreBookingTermsMessage: {
      id: 'morTerms.vioDisclaimerWL',
      defaultMessage:
        'Vio.com will process and assist with your booking. By continuing, you accept our <OurTerms></OurTerms>, <OurPrivacyPolicy></OurPrivacyPolicy>, and Vio.com’s <VioTerms></VioTerms>.'
    },
    merchantPostBookingTermsMessage: {
      id: 'morTerms.postBookingVioDisclaimerWL',
      defaultMessage:
        'Vio.com processes and assists with your booking. This booking is covered by our <OurTerms></OurTerms> and <OurPrivacyPolicy></OurPrivacyPolicy>, and Vio.com’s <VioTerms></VioTerms>.'
    },
    merchantPostBookingTermsWithDisclosureMessage: {
      id: 'morTerms.postBookingVioDisclaimerWLWithDisclosure',
      defaultMessage:
        'Vio.com processes and assists with your booking. This booking is covered by our <OurTerms></OurTerms>, <OurDisclosure></OurDisclosure> and <OurPrivacyPolicy></OurPrivacyPolicy>, and Vio.com’s <VioTerms></VioTerms>.'
    },
    soldOutLabelMessage: {
      id: 'roomOffer.soldOutMessage',
      defaultMessage: 'Sold out on {brandName}'
    },
    howItWorksMessage: {
      id: 'wl.homePageHowBrandWorks',
      defaultMessage: 'How {brandName} works'
    },
    homeFeaturesTitleMessage: {
      id: 'wl.homePageFeaturesTitle',
      defaultMessage: '<span>Stop missing out</span> on a better deal'
    },
    homeFeaturesSubtitleMessage: {
      id: 'wl.homePageFeaturesSubtitle',
      defaultMessage:
        'Access smart tools that help you book the best option every time.'
    },
    homeFeatureDealFreezeMessage: {
      id: 'wl.homePageFeatureHoldRoomAndPrice',
      defaultMessage: 'Hold the room and the price while you decide'
    },
    homeFeatureCombineRoomsMessage: {
      id: 'wl.homePageFeatureCombineRooms',
      defaultMessage:
        'Combine rooms to unlock additional savings and availability'
    },
    homeFeaturePayLaterMessage: {
      id: 'wl.homePageFeaturePayWhenItSuitsYou',
      defaultMessage: 'Book now, pay when it suits you'
    },
    homeFeaturePriceAlertsMessage: {
      id: 'wl.homePageFeaturePriceChangeAlerts',
      defaultMessage: 'Get notified when prices change'
    },
    homeFeaturePriceCalendarMessage: {
      id: 'homePage.powerFeaturesComparePricesInCalendar',
      defaultMessage: 'Compare prices<br></br>in the calendar'
    },
    homeValuePropsTitleMessage: {
      id: 'wl.homePageValuePropsTitle',
      defaultMessage: 'Search, compare, and <span>book with confidence</span>'
    },
    homeValuePropsExclusiveDealsTitleMessage: {
      id: 'wl.homePageValuePropsExclusiveDealsTitle',
      defaultMessage: 'Access exclusive deals'
    },
    homeValuePropsExclusiveDealsSubtitleMessage: {
      id: 'wl.homePageValuePropsExclusiveDealsSubtitle',
      defaultMessage: 'and savings you can’t find elsewhere'
    },
    homeValuePropsCompareSitesTitleMessage: {
      id: 'wl.homePageValuePropsCompareSitesTitle',
      defaultMessage: 'Compare leading travel sites'
    },
    homeValuePropsCompareSitesSubtitleMessage: {
      id: 'wl.homePageValuePropsCompareSitesSubtitle',
      defaultMessage: 'like Expedia and Agoda side by side'
    },
    homeValuePropsBookTheBestTitleMessage: {
      id: 'wl.homePageValuePropsBookTheBestTitle',
      defaultMessage: 'Book the best one'
    },
    homeValuePropsBookTheBestSubtitleMessage: {
      id: 'wl.homePageValuePropsBookTheBestSubtitle',
      defaultMessage: 'with us or one of our partners'
    },
    homeSupportTitleMessage: {
      id: 'wl.homePageSupportAlwaysHereTitle',
      defaultMessage: '<span>Customer Support 24/7</span> – always here to help'
    },
    homeSupportTalkToExpertsTitleMessage: {
      id: 'wl.homePageSupportTalkToExpertsTitle',
      defaultMessage: 'Talk to real travel experts'
    },
    homeSupportTalkToExpertsSubtitleMessage: {
      id: 'wl.homePageSupportTalkToExpertsSubtitle',
      defaultMessage: 'Our support team can answer all your booking questions.'
    },
    homeSupportBeforeDuringAfterTitleMessage: {
      id: 'wl.homePageSupportBeforeDuringAfterTitle',
      defaultMessage: 'Support before, during, and after your stay'
    },
    homeSupportBeforeDuringAfterSubtitleMessage: {
      id: 'wl.homePageSupportBeforeDuringAfterSubtitle',
      defaultMessage: 'Have a question or problem? Just get in touch.'
    },
    homeSupportOnYourSideTitleMessage: {
      id: 'wl.homePageSupportOnYourSideTitle',
      defaultMessage: 'We’re on your side'
    },
    homeSupportOnYourSideSubtitleMessage: {
      id: 'wl.homePageSupportOnYourSideSubtitle',
      defaultMessage: 'We’ll always try to get it right for you.'
    },
    redirectGreatChoiceMessage: {
      id: 'redirectGreatChoice',
      defaultMessage: 'You made a great choice'
    },
    deleteAccountTitleMessage: {
      id: 'membership.permanentlyDeleteMyAccount',
      defaultMessage: 'Permanently delete my account'
    }
  }),
  isInternal: false,
  tierLabel: TIER_LABELS.PrivateDeal,
  vclidCookieExpirationHours: 365 * 24 * 2,
  hideInternalRedirect: false,
  bovioProdApiKey: 'fDCbMdVRDPig83dTkmqhpnLrhsBkCMKHzkR',
  bovioStagingApiKey: 'fDCbMdVRDPig83dTkmqhpnLrhsBkCMKHzkR_test',
  shouldAugmentBookUriWithOtaModeProfile: false,
  capabilities: {
    search: {
      hasMap: true,
      hasFilters: true,
      hasSort: true,
      hasDesktopMoreDealsButton: true,
      hasStreetView: false,
      hasSeoFaqs: false,
      hasSeoUrls: false,
      showsAnchorCityName: false,
      hasBookingHint: {enabled: true, config: {trigger: 'urlParam'}},
      skipLabelPersistence: false,
      hasSeoSrpFlow: false,
      hasIndexableApSeo: false,
      hasCityLandingPage: false,
      hasTrustBanners: false,
      indexesHomepageOnly: false,
      quickFilterPresets: {starRatings: ['4', '5']},
      hasExpediaBackTab: false,
      homeTopDealsDestinations: null
    },
    shell: {
      hasAlternativeSearchForm: {enabled: false},
      hasFlexSearchFormContainer: false,
      hasCollapsingHeader: false,
      hasCustomHeader: false,
      hasCustomFooter: false,
      hasDarkMobileHeader: false,
      hasFullBrandFooter: false,
      hasHomeMarketingSections: false,
      homeFeatureTiles: [
        'dealFreeze',
        'combineRooms',
        'payLater',
        'priceAlerts'
      ],
      homeFeatureTileImagesPath: null,
      hasHomeSavingsCounter: false,
      homeSupportPhotosPath: null,
      hasHomeSubheading: true,
      homeHeroLayout: 'compact',
      supportsInAppFirst: false,
      usesEmbeddedFlow: false,
      mobileLogoFixedHeight: null,
      mobileSearchHeaderHeight: null,
      searchTopBarBubble: 'standard',
      compactHeroMobileImage: null,
      hasHeroSocialProof: false,
      hasMarketingHeroGradient: false,
      marketingHeroBackgroundImage: null,
      marketingHeroImage: null,
      searchTopBarHeroImage: null,
      showsSearchTopBarHotelsLabel: false,
      hasNeutralMobileMenuText: false,
      hasHomeStickyHeader: false,
      hasHomeDesktopSupportButton: false,
      hasFriendlyEmptyState: false,
      hasFriendlyErrorIllustration: false,
      hasRoundedSearchSubmitButton: false,
      logoHasIconText: false,
      showSocialMediaLinks: false,
      hasTrustpilot: false,
      showsHomeSrpLinks: false,
      hasHomeComingSoonSection: false,
      hasDesktopGoBackLink: false,
      hasAccountSideMenu: true,
      showsHowItWorksLink: false,
      hasNeutralMobileHeaderIcon: false
    },
    monetisation: {
      offersLockedByDefault: true,
      onlyOnBrandTag: {
        enabled: true,
        config: {
          message: defineMessage({
            id: 'brandLabelFinal',
            defaultMessage: 'Only on {brandName}'
          }),
          filterSectionLabel: defineMessage({
            id: 'brandLabelFinal',
            defaultMessage: 'Only on {brandName}'
          })
        }
      },
      cashback: {enabled: false},
      hasBrandedOffers: false,
      showsHopperAds: false,
      hasPointsProgram: false,
      hasEarnedPoints: false
    },
    retention: {
      hasExitPopup: true,
      hasPriceWatch: false,
      hasMarketingNotifications: false,
      hasNotificationOptIn: false,
      hasGetTheApp: false,
      hasWeb2App: false,
      hasDealFreeze: false,
      hasMyListFraming: false,
      hasDealFreezeFilter: false,
      appBannerStyle: 'iconBadge'
    },
    tracking: {
      emails: undefined,
      hasCioTracking: false,
      googleAnalytics: {enabled: false}
    },
    compliance: {
      cookieConsent: {enabled: false},
      hasLegalPages: false
    },
    commerce: {
      isOTAMode: false,
      bookOnBrand: {enabled: false},
      coBranded: {enabled: false},
      showVioRoomsOnAp: false,
      showVioBrandedRoomsTitle: false,
      hasCallToBook: false,
      hidesAppLockedDeals: false
    },
    bookings: {
      hasMyBookings: false,
      hasInvoiceFooter: false,
      hasPostStayNps: false,
      hasReviewForm: false,
      hasAutofillBilling: false
    },
    auth: {
      showSignIn: false,
      hasOidcProvider: false,
      hasGoogleOneTap: false,
      hasAvatarMemberTag: false,
      hasAccountPreferences: false,
      hasEmailPreferences: false,
      hasGetHelpLink: false,
      showUserWidget: false,
      showMobileUserAvatar: false,
      showDesktopSignInCta: false,
      showSignInToComplete: false,
      requiresSignInForCheckout: false,
      hasEmailOtpSignup: false,
      usesReferralStyleUnlockPanel: false,
      hasExternalLoginTrigger: {enabled: false}
    },
    internal: {
      allowsDevTools: false,
      hasOfferModeSwitcher: false
    }
  }
}

/**
 * The configuration for each brand that is available in the website.
 * It includes the brand name, site addresses, provider code, level, theme, links, feature flags and other brand-specific settings.
 */
export const brands: Record<BrandCode, Brand> = {
  vio: {
    ...baseConfig,
    code: 'vio',
    name: 'Vio',
    siteAddresses: {
      address: 'https://www.vio.com',
      checkoutAddress: 'https://secure.vio.com'
    },
    environmentAddresses: {
      stagingAddress: 'https://tst.vio.com',
      stagingCheckoutAddress: 'https://checkout.stag.bofh.fih.io',
      e2eAddress: 'https://vio-website-e2e.vercel.internal.vio.com',
      e2eCheckoutAddress:
        'https://website-checkout-backend.main.e2e.sez.cp.internal.vio.com'
    },
    providerCode: 'vio',
    tagline: 'Book like an insider',
    metaDescription:
      'We show real hotel prices across 100+ sites, compare options transparently, and help travelers book with confidence.',
    messages: {
      ...baseConfig.messages,
      ...defineMessages({
        pageTitleMessage: {
          id: 'browser.title',
          defaultMessage:
            'Book like an insider | Clear comparison, no hidden fees, real hotel deals'
        },
        pageTitleTagLineMessage: {
          id: 'browser.titleTagline',
          defaultMessage: 'Book like an insider'
        },
        homepageHeadingMessage: {
          id: 'bookLikeAnInsider',
          defaultMessage: 'Book like an insider'
        },
        taxesDisclaimerMessage: {
          id: 'taxesAndFees.taxesAndFeesCaption',
          defaultMessage:
            'Taxes and fees are generally recovery charges that Vio.com pays to its partners (e.g. hotels).'
        },
        merchantPreBookingTermsMessage: {
          id: 'morTerms.vioDisclaimer',
          defaultMessage:
            'By continuing, you accept our <OurTerms></OurTerms> and <OurPrivacyPolicy></OurPrivacyPolicy>.'
        },
        merchantPostBookingTermsMessage: {
          id: 'morTerms.postBookingVioDisclaimer',
          defaultMessage:
            'Vio.com processes and assists with your booking. This booking is covered by our <OurTerms></OurTerms> and <OurPrivacyPolicy></OurPrivacyPolicy>.'
        },
        merchantPostBookingTermsWithDisclosureMessage: {
          id: 'morTerms.postBookingVioDisclaimerWithDisclosure',
          defaultMessage:
            'Vio.com processes and assists with your booking. This booking is covered by our <OurTerms></OurTerms>, <OurDisclosure></OurDisclosure> and <OurPrivacyPolicy></OurPrivacyPolicy>.'
        },
        redirectGreatChoiceMessage: {
          id: 'redirectGreatChoiceVio',
          defaultMessage: "You're booking like an insider"
        },
        deleteAccountTitleMessage: {
          id: 'permanentlyDeleteAccount',
          defaultMessage: 'Permanently delete my Vio.com account'
        },
        howItWorksMessage: {
          id: 'howVioWorks',
          defaultMessage: 'How Vio Works'
        },
        homeFeaturesTitleMessage: {
          id: 'homePage.powerFeaturesTitle',
          defaultMessage: '<span>Never miss out</span> on a great deal again'
        },
        homeFeaturesSubtitleMessage: {
          id: 'homePage.powerFeaturesSubtitle',
          defaultMessage:
            'Our powerful features help you book the best place at the best price, every time.'
        },
        homeFeatureDealFreezeMessage: {
          id: 'homePage.powerFeaturesReservePriceForFreeBookWhenYourReady',
          defaultMessage:
            'Reserve a price for free,<br></br>book when you’re ready'
        },
        homeFeatureCombineRoomsMessage: {
          id: 'homePage.powerFeaturesCombineRoomsToFindHiddenSavings',
          defaultMessage:
            'Combine rooms to find<br></br>hidden savings and availability'
        },
        homeFeaturePayLaterMessage: {
          id: 'homePage.powerFeaturesBookNowChooseWhenToPay',
          defaultMessage: 'Book now,<br></br>choose when to pay'
        },
        homeFeaturePriceAlertsMessage: {
          id: 'homePage.trackPricesAtYourFavouriteHotel',
          defaultMessage: 'Track prices at your<br></br>favourite hotels'
        },
        homeValuePropsExclusiveDealsTitleMessage: {
          id: 'homePage.savingExclusiveDealsTitle',
          defaultMessage: 'We find exclusive deals'
        },
        homeValuePropsExclusiveDealsSubtitleMessage: {
          id: 'homePage.savingExclusiveDealsSubtitle',
          defaultMessage: 'with discounts up to 45%'
        },
        homeValuePropsCompareSitesTitleMessage: {
          id: 'homePage.savingCompareTravelSitesTitle',
          defaultMessage: 'And compare top travel sites'
        },
        homeValuePropsCompareSitesSubtitleMessage: {
          id: 'homePage.savingCompareTravelSitesSubtitle',
          defaultMessage: 'like Expedia and Agoda'
        },
        homeValuePropsBookTheBestTitleMessage: {
          id: 'homePage.savingYouBookTheBestTitle',
          defaultMessage: 'Then you book the best'
        },
        homeValuePropsBookTheBestSubtitleMessage: {
          id: 'homePage.savingYouBookTheBestSubtitle',
          defaultMessage: 'with us or another site'
        },
        homeSupportTitleMessage: {
          id: 'homePage.supportTitle',
          defaultMessage:
            '<span>Exceptional</span> customer support <span>24/7</span>'
        },
        homeSupportTalkToExpertsTitleMessage: {
          id: 'homePage.supportRealTravelExpertsTitle',
          defaultMessage: 'Real travel experts'
        },
        homeSupportTalkToExpertsSubtitleMessage: {
          id: 'support.RealTravelExpertsSubtitle',
          defaultMessage: "We've been there, done it, got the T-shirt."
        },
        homeSupportBeforeDuringAfterTitleMessage: {
          id: 'homePage.supportAlwaysThereForYouTitle',
          defaultMessage: 'Always there for you'
        },
        homeSupportBeforeDuringAfterSubtitleMessage: {
          id: 'homePage.supportAlwaysThereForYouSubtitle',
          defaultMessage:
            'Our response times are among the fastest in the industry.'
        },
        homeSupportOnYourSideTitleMessage: {
          id: 'homePage.supportEfficientAnEffectiveTitle',
          defaultMessage: 'Efficient and effective'
        },
        homeSupportOnYourSideSubtitleMessage: {
          id: 'homePage.supportEfficientSubtitle',
          defaultMessage: "We do the hard work, so you don't have to."
        }
      })
    },
    meta: {
      'facebook-domain-verification': 'c0vnjgvsd2xx1cegt4tqy5q7v5ksz5'
    },
    level: 'advanced',
    isInternal: true,
    tierLabel: TIER_LABELS.Gems,
    hideInternalRedirect: true,
    theme: 'vio',
    links: {
      aboutUsUrl: 'https://www.vio.com/about',
      careersUrl: 'https://www.vio.com/careers',
      developersUrl: 'https://www.vio.com/affiliates',
      partnershipsUrl: 'mailto:partners@vio.com',
      blogUrl: 'https://www.vio.com/blog',
      helpUrl: 'https://support.vio.com/',
      privacyPolicyUrl:
        'https://support.vio.com/hc/en-us/articles/37314221746461-Privacy-Policy',
      cookiePolicyUrl:
        'https://support.vio.com/hc/en-us/articles/37508750538909-Cookie-Policy',
      termsOfUseUrl:
        'https://support.vio.com/hc/en-us/articles/37509195342109-Terms-of-Use',
      disclosureUrl: 'https://www.vio.com/hc/en-us/il-transactional-disclosure',
      contactUrl:
        'https://support.vio.com/hc/en-us/requests/new?ticket_form_id=6796130090397',
      howItWorksUrl: 'https://www.vio.com/how-vio-works',
      supportEmail: 'support@vio.com',
      howToCancelUrl:
        'https://support.vio.com/hc/en-us/articles/36907207638429',
      priceDiscrepancyUrl:
        'https://support.vio.com/hc/en-us/articles/38311701621533'
    },
    socials: {
      facebookUrl: 'https://www.facebook.com/viodotcom',
      linkedinUrl: 'https://nl.linkedin.com/company/viodotcom',
      instagramUrl: 'https://www.instagram.com/vio.com.travel/'
    },
    isLive: true,
    customerServiceLocales: ['es', 'fr', 'de', 'it', 'ar'],
    capabilities: {
      ...baseConfig.capabilities,
      internal: {
        ...baseConfig.capabilities.internal,
        hasOfferModeSwitcher: true
      },
      commerce: {
        ...baseConfig.capabilities.commerce,
        showVioRoomsOnAp: true,
        showVioBrandedRoomsTitle: true,
        hasCallToBook: true,
        bookOnBrand: {
          enabled: true,
          config: {
            helpCenterChatKeyProd: '0746f4de-c65a-45e5-b8cb-3d8378058827',
            helpCenterChatKeyStaging: '63ab78c1-2a7f-4c4a-a886-cf75248cddc1'
          }
        },
        hidesAppLockedDeals: true
      },
      bookings: {
        hasMyBookings: true,
        hasInvoiceFooter: true,
        hasPostStayNps: true,
        hasReviewForm: true,
        hasAutofillBilling: true
      },
      monetisation: {
        ...baseConfig.capabilities.monetisation,
        onlyOnBrandTag: {
          enabled: true,
          config: {
            message: defineMessage({
              id: 'vioLabelFinal',
              defaultMessage: 'Only on Vio'
            }),
            filterSectionLabel: defineMessage({
              id: 'onlyOnVio.title',
              defaultMessage: 'Book like an insider'
            })
          }
        },
        hasBrandedOffers: true,
        showsHopperAds: true
      },
      search: {
        ...baseConfig.capabilities.search,
        hasStreetView: true,
        hasSeoFaqs: true,
        hasSeoUrls: true,
        skipLabelPersistence: true,
        hasSeoSrpFlow: true,
        hasIndexableApSeo: true,
        hasCityLandingPage: true,
        hasTrustBanners: true,
        indexesHomepageOnly: false,
        hasExpediaBackTab: true
      },
      shell: {
        ...baseConfig.capabilities.shell,
        hasFullBrandFooter: true,
        hasHomeMarketingSections: true,
        homeFeatureTiles: [
          'dealFreeze',
          'combineRooms',
          'payLater',
          'priceAlerts',
          'priceCalendar'
        ],
        hasHomeSavingsCounter: true,
        homeHeroLayout: 'marketing',
        hasHeroSocialProof: true,
        marketingHeroBackgroundImage: 'hero-section/vio-trunk.svg',
        marketingHeroImage: {
          src: 'hero-section/insider.webp',
          width: 351,
          height: 454
        },
        hasHomeStickyHeader: true,
        hasHomeDesktopSupportButton: true,
        hasFriendlyEmptyState: true,
        hasFriendlyErrorIllustration: true,
        hasRoundedSearchSubmitButton: true,
        logoHasIconText: true,
        showSocialMediaLinks: true,
        hasTrustpilot: true,
        showsHomeSrpLinks: true,
        showsHowItWorksLink: clientOnly<
          boolean,
          Pick<CapabilityContext, 'locale'>
        >(ctx => ctx.locale.startsWith('en')),
        searchTopBarBubble: 'branded',
        hasNeutralMobileMenuText: true,
        hasNeutralMobileHeaderIcon: true
      },
      retention: {
        ...baseConfig.capabilities.retention,
        hasPriceWatch: true,
        hasMarketingNotifications: true,
        hasNotificationOptIn: true,
        hasGetTheApp: true,
        hasWeb2App: true,
        hasDealFreeze: true,
        hasMyListFraming: true,
        hasDealFreezeFilter: clientOnly<boolean, RetentionCapabilityContext>(
          ctx => ctx.experimentVariant('c747927f-deal-freeze-srp') === 'b'
        ),
        appBannerStyle: 'plain'
      },
      tracking: {
        emails: {
          enable: true,
          cioWorkspace: 'VIO',
          defaultLocale: 'en-US',
          cioTrackingSiteIds: {
            development: '3f5028f5275d0cef97bd',
            review: '3f5028f5275d0cef97bd',
            staging: '3f5028f5275d0cef97bd',
            e2e: '3f5028f5275d0cef97bd',
            production: '95ba2d8ee65cf9a9be06',
            test: '3f5028f5275d0cef97bd'
          }
        },
        hasCioTracking: true,
        googleAnalytics: {enabled: false}
      },
      compliance: {
        ...baseConfig.capabilities.compliance,
        cookieConsent: {
          enabled: true,
          config: {id: 'b697fff4-f9e2-4ae1-9eea-e38ef1e470f1'}
        },
        hasLegalPages: true
      },
      auth: {
        ...baseConfig.capabilities.auth,
        showSignIn: true,
        hasGoogleOneTap: true,
        hasAvatarMemberTag: true,
        hasAccountPreferences: true,
        hasEmailPreferences: true,
        hasGetHelpLink: true,
        showUserWidget: true,
        showMobileUserAvatar: true,
        showDesktopSignInCta: true,
        showSignInToComplete: true,
        hasEmailOtpSignup: true
      }
    }
  },
  findhotel: {
    ...baseConfig,
    code: 'findhotel',
    name: 'FindHotel',
    siteAddresses: {
      address: 'https://findhotel.net',
      checkoutAddress: 'https://secure.vio.com'
    },
    providerCode: 'fht',
    tagline: 'Find the right hotel at the best price',
    metaDescription: 'Find the right hotel at the best price',
    meta: {
      'facebook-domain-verification': 'gkn75b19c2r26kvgpfphuua9c5qn1u'
    },
    level: 'advanced',
    isInternal: true,
    theme: 'vio',
    links: {
      privacyPolicyUrl:
        'https://support.vio.com/hc/en-us/articles/37314221746461-Privacy-Policy',
      termsOfUseUrl:
        'https://support.vio.com/hc/en-us/articles/37509195342109-Terms-of-Use'
    },
    isLive: false,
    capabilities: {
      ...baseConfig.capabilities,
      search: {
        ...baseConfig.capabilities.search,
        skipLabelPersistence: true,
        hasExpediaBackTab: true
      },
      commerce: {
        ...baseConfig.capabilities.commerce,
        showVioRoomsOnAp: true,
        hasCallToBook: true,
        bookOnBrand: {enabled: true, config: {}}
      },
      monetisation: {
        ...baseConfig.capabilities.monetisation,
        showsHopperAds: true
      },
      auth: {
        ...baseConfig.capabilities.auth,
        showSignIn: true,
        showUserWidget: true
      }
    }
  },
  etrip: {
    ...baseConfig,
    code: 'etrip',
    name: 'Etrip',
    siteAddresses: {
      address: 'https://etrip.net'
    },
    providerCode: 'etr',
    meta: {
      'facebook-domain-verification': 'tgmfj8y91tp7tpg8u5mvz1925zby0y'
    },
    level: 'advanced',
    isInternal: true,
    theme: 'vio',
    links: {
      aboutUsUrl:
        'https://support.etrip.net/hc/en-us/articles/10555108760349-About-us',
      privacyPolicyUrl:
        'https://support.etrip.net/hc/en-us/articles/10555195541533-Privacy-Policy',
      termsOfUseUrl:
        'https://support.etrip.net/hc/en-us/articles/10555116393757-Terms-of-use'
    },
    isLive: true,
    shouldAugmentBookUriWithOtaModeProfile: true,
    capabilities: {
      ...baseConfig.capabilities,
      search: {
        ...baseConfig.capabilities.search,
        skipLabelPersistence: true,
        indexesHomepageOnly: false,
        hasExpediaBackTab: true
      },
      shell: {
        ...baseConfig.capabilities.shell,
        hasDarkMobileHeader: true
      },
      commerce: {
        ...baseConfig.capabilities.commerce,
        showVioRoomsOnAp: false,
        hasCallToBook: true
      },
      monetisation: {
        ...baseConfig.capabilities.monetisation,
        showsHopperAds: true
      },
      compliance: {
        ...baseConfig.capabilities.compliance,
        cookieConsent: {
          enabled: true,
          config: {id: '6ed51359-d8f7-433c-854f-6434c8c01a6f'}
        }
      },
      auth: {
        ...baseConfig.capabilities.auth,
        showSignIn: true,
        showUserWidget: true,
        usesReferralStyleUnlockPanel: true
      }
    }
  },
  goseek: {
    ...baseConfig,
    code: 'goseek',
    name: 'goSeek.com',
    siteAddresses: {
      address: 'https://www.goseek.com'
    },
    providerCode: 'gsk',
    level: 'basic',
    isInternal: true,
    theme: 'goseek',
    links: {
      aboutUsUrl:
        'https://goseek.zendesk.com/hc/en-us/articles/11185399104541-About-us',
      privacyPolicyUrl:
        'https://goseek.zendesk.com/hc/en-us/articles/11185467302557-Privacy-Policy',
      termsOfUseUrl:
        'https://goseek.zendesk.com/hc/en-us/articles/11185418486301-Terms-of-use'
    },
    isLive: true,
    shouldAugmentBookUriWithOtaModeProfile: true,
    capabilities: {
      ...baseConfig.capabilities,
      search: {
        ...baseConfig.capabilities.search,
        hasMap: false,
        hasFilters: false,
        hasSort: false,
        hasDesktopMoreDealsButton: false,
        skipLabelPersistence: true,
        indexesHomepageOnly: false,
        hasExpediaBackTab: true
      },
      shell: {
        ...baseConfig.capabilities.shell,
        hasDarkMobileHeader: true
      },
      commerce: {
        ...baseConfig.capabilities.commerce,
        showVioRoomsOnAp: false,
        hasCallToBook: true
      },
      monetisation: {
        ...baseConfig.capabilities.monetisation,
        showsHopperAds: true
      },
      compliance: {
        ...baseConfig.capabilities.compliance,
        cookieConsent: {
          enabled: true,
          config: {id: '76ddf610-e404-45f2-9329-d3b88937c6c4'}
        }
      },
      auth: {
        ...baseConfig.capabilities.auth,
        showSignIn: true,
        showUserWidget: true
      }
    }
  },
  dealbase: {
    ...baseConfig,
    code: 'dealbase',
    name: 'dealbase.com',
    messages: {
      ...baseConfig.messages,
      homepageHeadingMessage: defineMessage({
        id: 'dealbase.homePageHeading',
        defaultMessage: 'Discover exclusive hotel deals'
      })
    },
    siteAddresses: {
      address: 'https://www.dealbase.com'
    },
    providerCode: 'dealbase',
    level: 'custom',
    isInternal: true,
    theme: 'dealbase',
    links: {
      aboutUsUrl:
        'https://dealbase.zendesk.com/hc/en-us/articles/18884421709981-About-us',
      privacyPolicyUrl:
        'https://dealbase.zendesk.com/hc/en-us/articles/18885895455645-Privacy-Policy',
      termsOfUseUrl:
        'https://dealbase.zendesk.com/hc/en-us/articles/18884421715613-Terms-of-use'
    },
    mainHotelImageIndex: 1,
    enabledFilters: [
      FilterId.PriceFilterPill,
      FilterId.PriceFilterPopover,
      FilterId.FreeCancellationPill,
      FilterId.ReviewScorePill,
      FilterId.ReviewScoreFilterPopover,
      FilterId.ReviewScoreQuickPill,
      FilterId.PropertyTypeFilterPill,
      FilterId.PropertyTypeFilterPopover,
      FilterId.StarRatingPill,
      FilterId.StarRatingFilterPopover
    ],
    isLive: true,
    shouldAugmentBookUriWithOtaModeProfile: true,
    capabilities: {
      ...baseConfig.capabilities,
      search: {
        ...baseConfig.capabilities.search,
        hasMap: false,
        hasDesktopMoreDealsButton: false,
        skipLabelPersistence: true,
        indexesHomepageOnly: false,
        hasExpediaBackTab: true
      },
      shell: {
        ...baseConfig.capabilities.shell,
        hasAlternativeSearchForm: {
          enabled: true,
          config: {
            hasTitle: true,
            hasSubtitle: false,
            hasFormTitle: false,
            hasOutsideInputLabels: false,
            hasTextInputPrefix: false,
            inputsGap: 's500',
            hasShadow: false
          }
        },
        hasCollapsingHeader: true,
        hasDarkMobileHeader: true
      },
      retention: {
        ...baseConfig.capabilities.retention,
        hasExitPopup: false
      },
      commerce: {
        ...baseConfig.capabilities.commerce,
        showVioRoomsOnAp: false,
        hasCallToBook: true
      },
      monetisation: {
        ...baseConfig.capabilities.monetisation,
        showsHopperAds: true
      },
      compliance: {
        ...baseConfig.capabilities.compliance,
        cookieConsent: {
          enabled: true,
          config: {id: '41c41b98-0a09-4073-95b5-fb99ad85cc89'}
        }
      },
      auth: {
        ...baseConfig.capabilities.auth,
        showSignIn: true,
        showUserWidget: true
      }
    }
  },
  kiwi: {
    ...baseConfig,
    code: 'kiwi',
    name: 'Kiwi.com',
    siteAddresses: {
      address: 'https://hotels.kiwi.com',
      checkoutAddress: 'https://securebooking.kiwi.com'
    },
    providerCode: 'kiwi',
    messages: {
      ...baseConfig.messages,
      ...defineMessages({
        homepageHeadingMessage: {
          id: 'wl.kwHomePageHeading',
          defaultMessage: 'You book for less'
        },
        homepageSubheadingMessage: {
          id: 'wl.kwHomePageSubHeading',
          defaultMessage: "Book cheap stays other sites simply can't find."
        },
        widgetHeaderMessage: {
          id: 'multipleHotelsWidgetWithSearch.kiwiHotels',
          defaultMessage: 'Kiwi.com Hotels'
        }
      })
    },
    level: 'custom',
    theme: 'kiwi',
    links: {
      helpUrl: 'https://kiwi-hotels.zendesk.com/hc/en-us',
      aboutUsUrl:
        'https://kiwi-hotels.zendesk.com/hc/en-us/sections/15086629933469-General',
      privacyPolicyUrl:
        'https://kiwi-hotels.zendesk.com/hc/en-us/articles/15086734253597-Privacy-Policy',
      termsOfUseUrl:
        'https://kiwi-hotels.zendesk.com/hc/en-us/articles/15086788552221-Terms-of-use',
      contactUrl: 'https://kiwi-hotels.zendesk.com/hc/en-us/requests/new',
      priceDiscrepancyUrl:
        'https://kiwi-hotels.zendesk.com/hc/en-us/articles/38312916135453'
    },
    partnerKeys: [
      'ZV0gASHJIyCG7SL9Nsqpnixp',
      '44coWO2ahfAmiM1XfTkrFdMh' // OTA mode enabled
    ],
    sapiCode: 'kiwi',
    hideInternalRedirect: true,
    isLive: false,
    vclidCookieExpirationHours: 4,
    capabilities: {
      ...baseConfig.capabilities,
      commerce: {
        ...baseConfig.capabilities.commerce,
        showVioRoomsOnAp: true,
        isOTAMode: true,
        bookOnBrand: {
          enabled: true,
          config: {
            helpCenterChatKeyProd: 'f9b2c829-cc48-4612-9258-d6b048b68fce',
            helpCenterChatKeyStaging: 'f9b2c829-cc48-4612-9258-d6b048b68fce'
          }
        }
      },
      bookings: {
        ...baseConfig.capabilities.bookings,
        hasMyBookings: true
      },
      search: {
        ...baseConfig.capabilities.search,
        showsAnchorCityName: true,
        hasBookingHint: {
          enabled: true,
          config: {trigger: 'directSrpLanding'}
        }
      },
      shell: {
        ...baseConfig.capabilities.shell,
        hasAlternativeSearchForm: {
          enabled: true,
          config: {
            hasTitle: false,
            hasSubtitle: false,
            hasOutsideInputLabels: false,
            hasTextInputPrefix: true,
            inputsGap: 's500',
            hasFormTitle: true,
            hasShadow: true,
            heroImage: 'kiwi-web-hero.svg',
            hasOffsetHeroImage: true,
            hasEmphasizedTitle: true,
            heroVerticalPadding: 'calc(var(--layout-spacing-s900) * 2)',
            messages: defineMessages({
              formTitle: {
                id: 'wl.kwHomePageHeading',
                defaultMessage: 'You book for less'
              }
            })
          }
        },
        hasCollapsingHeader: true,
        hasCustomHeader: true,
        hasDesktopGoBackLink: true,
        hasAccountSideMenu: false,
        // Kiwi renders the search form in the flex container with pill inputs.
        hasFlexSearchFormContainer: true,
        // Kiwi's hero intentionally omits the homepage subheading (sc-186137).
        hasHomeSubheading: false,
        // Kiwi runs the WL inside its native app's in-app browser (sc-185842).
        supportsInAppFirst: true,
        // Kiwi runs an embedded in-app flow (sc-186141).
        usesEmbeddedFlow: true,
        // Kiwi's logo is bulky, so the mobile header pins it to 40px (sc-186140).
        mobileLogoFixedHeight: 40,
        mobileSearchHeaderHeight: 182,
        searchTopBarBubble: 'none',
        compactHeroMobileImage: 'kiwi-web-hero-mobile.svg',
        searchTopBarHeroImage: 'kiwi-web-hero-mobile.svg',
        showsSearchTopBarHotelsLabel: true
      },
      monetisation: {
        ...baseConfig.capabilities.monetisation,
        offersLockedByDefault: false
      },
      retention: {
        ...baseConfig.capabilities.retention,
        hasExitPopup: false,
        hasPriceWatch: true,
        hasDealFreeze: true,
        hasMyListFraming: true
      },
      tracking: {
        ...baseConfig.capabilities.tracking,
        emails: {
          enable: true,
          cioWorkspace: 'KIWI',
          defaultLocale: 'en-US',
          cioTrackingSiteIds: {
            development: '6a9a714e5e604d40bbc0',
            review: '6a9a714e5e604d40bbc0',
            staging: '6a9a714e5e604d40bbc0',
            // Off, because this brand's other environments all use its production workspace.
            e2e: '',
            production: '6a9a714e5e604d40bbc0',
            test: '6a9a714e5e604d40bbc0'
          }
        }
      },
      compliance: {
        ...baseConfig.capabilities.compliance,
        cookieConsent: {
          enabled: true,
          config: {id: '6b39aa0b-c95d-4de2-adbe-bf185f3b3e87'}
        }
      },
      auth: {
        ...baseConfig.capabilities.auth,
        showSignIn: true,
        hasOidcProvider: true,
        showUserWidget: true
      }
    }
  },
  kayak: {
    ...baseConfig,
    code: 'kayak',
    name: 'Kayak',
    siteAddresses: {
      address: 'https://kayak.vio.com'
    },
    providerCode: 'kayak',
    level: 'custom',
    theme: 'kayak',
    links: {
      aboutUsUrl: 'https://www.kayak.com/about',
      privacyPolicyUrl: 'https://www.kayak.com/privacy',
      termsOfUseUrl: 'https://www.kayak.com/terms-of-use'
    },
    sapiCode: 'kayak',
    isLive: false
  },
  holidaypiratesde: {
    ...baseConfig,
    code: 'holidaypiratesde',
    domainCode: 'urlaubspiraten',
    name: 'Urlaubspiraten',
    siteAddresses: {
      address: 'https://hotels.urlaubspiraten.de/',
      checkoutAddress: 'https://securebooking.urlaubspiraten.de',
      logoRedirectUrl: 'https://urlaubspiraten.de'
    },
    providerCode: 'holidaypiratesde',
    partnerKeys: ['wO5RobF6eR4jOQwTyMzBSvBa'],
    level: 'custom',
    theme: 'holidaypirates',
    hideInternalRedirect: true,
    isLive: false,
    links: {
      helpUrl: 'https://hotelsurlaubspiraten.zendesk.com/hc/en-us',
      termsOfUseUrl:
        'https://hotelsurlaubspiraten.zendesk.com/hc/en-us/articles/24784636758301-Terms-of-use',
      privacyPolicyUrl:
        'https://hotelsurlaubspiraten.zendesk.com/hc/en-us/articles/24749882756509-Datenschutzerkl%C3%A4rung',
      contactUrl:
        'https://hotelsurlaubspiraten.zendesk.com/hc/en-us/requests/new',
      priceDiscrepancyUrl:
        'https://hotelsurlaubspiraten.zendesk.com/hc/en-us/articles/38312858126365'
    },
    subBrand: {
      enabled: true,
      brands: {
        Holidaypirates: {
          subBrandCode: 'holidaypirates',
          partnerKey: 'J5V1gOcgdU9w7miShKqGGShS',
          name: 'HolidayPirates',
          defaultCurrency: 'GBP',
          defaultLanguage: 'en-GB',
          headerScriptDomain: 'https://www.holidaypirates.com',
          market: 'uk'
        },
        Wakacyjnipiraci: {
          subBrandCode: 'wakacyjnipiraci',
          partnerKey: 'DtzLmPQWplZfeTdBHifCYpPc',
          name: 'Wakacyjnipiraci',
          defaultCurrency: 'PLN',
          defaultLanguage: 'pl',
          headerScriptDomain: 'https://www.wakacyjnipiraci.pl',
          market: 'pl'
        },
        Piratinviaggio: {
          subBrandCode: 'piratinviaggio',
          partnerKey: 'Xe1DeO9LS4DdPUOBPvVxk5Zi',
          name: 'PiratinViaggio',
          defaultCurrency: 'EUR',
          defaultLanguage: 'it',
          headerScriptDomain: 'https://www.piratinviaggio.it',
          market: 'it'
        },
        Voyagespirates: {
          subBrandCode: 'voyagespirates',
          partnerKey: 'Sss4wL2voG9Qvzajg7zxdk9J',
          name: 'VoyagesPirates',
          defaultCurrency: 'EUR',
          defaultLanguage: 'fr',
          headerScriptDomain: 'https://www.voyagespirates.fr',
          market: 'fr'
        },
        Vakantiepiraten: {
          subBrandCode: 'vakantiepiraten',
          partnerKey: '5IY8NGIfS94e5AcP5flLicWz',
          name: 'VakantiePiraten',
          defaultCurrency: 'EUR',
          defaultLanguage: 'nl',
          headerScriptDomain: 'https://www.vakantiepiraten.nl',
          market: 'nl'
        },
        Viajerospiratas: {
          subBrandCode: 'viajerospiratas',
          partnerKey: '5037Pp4wvIfNoMpxIckp7zUy',
          name: 'ViajerosPiratas',
          defaultCurrency: 'EUR',
          defaultLanguage: 'es',
          headerScriptDomain: 'https://www.viajerospiratas.es',
          market: 'es'
        },
        Ferienpiraten: {
          subBrandCode: 'ferienpiraten',
          partnerKey: 'KaKSmkee8sDkCI9uSqts3TQB',
          name: 'FerienPiraten',
          defaultCurrency: 'CHF',
          defaultLanguage: 'de',
          headerScriptDomain: 'https://www.ferienpiraten.ch',
          market: 'ch'
        },
        UrlaubspiratenDE: {
          subBrandCode: 'urlaubspiratende',
          partnerKey: 'PFOkWMD8KScyXQY16QuZQywy',
          name: 'Urlaubspiraten',
          defaultCurrency: 'EUR',
          defaultLanguage: 'de',
          headerScriptDomain: 'https://www.urlaubspiraten.de',
          market: 'de'
        },
        UrlaubspiratenAT: {
          subBrandCode: 'urlaubspiratenat',
          partnerKey: 'p4sgHYmB24W4u9pwpXdwdm3U',
          name: 'Urlaubspiraten',
          defaultCurrency: 'EUR',
          defaultLanguage: 'de',
          headerScriptDomain: 'https://www.urlaubspiraten.at',
          market: 'at'
        },
        Travelpirates: {
          subBrandCode: 'travelpirates',
          partnerKey: 'rfEtjKXTDPCnRSi8VBbYHMZt',
          name: 'TravelPirates',
          defaultCurrency: 'USD',
          defaultLanguage: 'en',
          headerScriptDomain: 'https://www.travelpirates.com',
          market: 'us'
        }
      }
    },
    analytics: {
      gtm: {
        containerId: 'GTM-MBS7DCVT'
      }
    },
    capabilities: {
      ...baseConfig.capabilities,
      commerce: {
        ...baseConfig.capabilities.commerce,
        showVioRoomsOnAp: true,
        bookOnBrand: {
          enabled: true,
          config: {
            helpCenterChatKeyProd: '4f9e91c1-aaa9-4759-ae87-a418df611852',
            helpCenterChatKeyStaging: '4f9e91c1-aaa9-4759-ae87-a418df611852',
            defaultLanguageCode: 'de',
            defaultCurrencyCode: 'EUR'
          }
        }
      },
      shell: {
        ...baseConfig.capabilities.shell,
        hasCollapsingHeader: true,
        hasCustomHeader: true,
        mobileSearchHeaderHeight: 205
      },
      monetisation: {
        ...baseConfig.capabilities.monetisation,
        offersLockedByDefault: false,
        onlyOnBrandTag: {
          enabled: true,
          config: {
            message: defineMessage({
              id: 'wl.hpExclusive',
              defaultMessage: 'Exclusive'
            }),
            filterSectionLabel: defineMessage({
              id: 'wl.hpExclusive',
              defaultMessage: 'Exclusive'
            })
          }
        }
      },
      tracking: {
        ...baseConfig.capabilities.tracking,
        emails: {
          enable: true,
          cioWorkspace: 'HOLIDAY_PIRATES',
          defaultLocale: 'de-DE'
        }
      },
      compliance: {
        ...baseConfig.capabilities.compliance,
        cookieConsent: {
          enabled: true,
          config: {id: '15e80c3a-8454-4aa9-b78f-fc941b5e74e0'}
        }
      },
      auth: {
        ...baseConfig.capabilities.auth,
        hasExternalLoginTrigger: {
          enabled: true,
          config: {triggerElementId: 'hp-login-button'}
        }
      }
    }
  },
  weward: {
    ...baseConfig,
    code: 'weward',
    domainCode: 'wewardapp',
    hideInternalRedirect: true,
    isLive: false,
    level: 'custom',
    links: {
      helpUrl: 'https://weward-hotels-by-vio.zendesk.com/hc/en-us',
      termsOfUseUrl:
        'https://weward-hotels-by-vio.zendesk.com/hc/en-us/articles/26848004560413-Terms-of-Use',
      privacyPolicyUrl:
        'https://weward-hotels-by-vio.zendesk.com/hc/en-us/articles/26844163324061-Privacy-Policy',
      contactUrl:
        'https://weward-hotels-by-vio.zendesk.com/hc/en-us/requests/new',
      priceDiscrepancyUrl:
        'https://weward-hotels-by-vio.zendesk.com/hc/en-us/articles/38311372629277'
    },
    messages: {
      ...baseConfig.messages,
      ...defineMessages({
        homepageHeadingMessage: {
          id: 'wl.wwHomePageHeading',
          defaultMessage: 'Find the perfect hotel at the best price'
        }
      })
    },
    name: 'WeWard',
    providerCode: 'weward',
    sapiCode: 'weward',
    siteAddresses: {
      address: 'https://hotels.wewardapp.com/',
      checkoutAddress: 'https://securebooking.wewardapp.com'
    },
    theme: 'weward',
    capabilities: {
      ...baseConfig.capabilities,
      commerce: {
        ...baseConfig.capabilities.commerce,
        showVioRoomsOnAp: true,
        bookOnBrand: {
          enabled: true,
          config: {
            helpCenterChatKeyProd: '27a33819-3774-4c37-bcca-bb156735501c',
            helpCenterChatKeyStaging: '27a33819-3774-4c37-bcca-bb156735501c'
          }
        }
      },
      monetisation: {
        ...baseConfig.capabilities.monetisation,
        offersLockedByDefault: false,
        cashback: {
          enabled: true,
          config: {
            isLongMessageLayout: false,
            alertVariant: 'warning',
            messages: defineMessages({
              cashbackLongMessage: {
                id: 'cashback.longMessage',
                defaultMessage: 'Earn {value}'
              },
              cashbackExplanationTitle: {
                id: 'cashbackExplanation.title',
                defaultMessage: 'How cashback works'
              },
              cashbackExplanationStep1Title: {
                id: 'cashbackExplanation.step1Title',
                defaultMessage: 'Choose the perfect deal'
              },
              cashbackExplanationStep1Description: {
                id: 'cashbackExplanation.step1Description',
                defaultMessage:
                  "Book directly through us or be redirected to a trusted partner to complete your booking. Either way, you're eligible for cashback."
              },
              cashbackExplanationStep2Title: {
                id: 'cashbackExplanation.step2Title',
                defaultMessage: 'Earn cashback automatically'
              },
              cashbackExplanationStep2Description: {
                id: 'cashbackExplanation.step2Description',
                defaultMessage:
                  'When you book, we earn a commission from the provider and share it with you as cashback. No codes or extra steps needed.'
              },
              cashbackExplanationStep3Title: {
                id: 'cashbackExplanation.step3Title',
                defaultMessage: 'Withdraw your cashback'
              },
              cashbackExplanationStep3Description: {
                id: 'cashbackExplanation.step3Description',
                defaultMessage:
                  'Your cashback will be ready to withdraw within 30 to 60 days of completing your stay.'
              }
            })
          }
        }
      },
      tracking: {
        ...baseConfig.capabilities.tracking,
        emails: {
          enable: true,
          cioWorkspace: 'WEWARD',
          defaultLocale: 'en-US'
        }
      },
      compliance: {
        ...baseConfig.capabilities.compliance,
        cookieConsent: {
          enabled: true,
          config: {id: 'f15d8ac2-7cc0-4eea-998a-c618a028a3c0'}
        }
      }
    }
  },
  ovago: {
    ...baseConfig,
    code: 'ovago',
    name: 'Ovago',
    siteAddresses: {
      address: 'https://hotels.ovago.com/',
      checkoutAddress: 'https://securebooking.ovago.com'
    },
    providerCode: 'ovago',
    level: 'custom',
    sapiCode: 'ovago',
    theme: 'ovago',
    links: {
      helpUrl: 'https://ovagosupport.zendesk.com/hc/en-us',
      termsOfUseUrl:
        'https://ovagosupport.zendesk.com/hc/en-us/articles/26456677350045-Terms-of-Use',
      privacyPolicyUrl:
        'https://ovagosupport.zendesk.com/hc/en-us/articles/26456803658909-Privacy-Policy',
      contactUrl: 'https://ovagosupport.zendesk.com/hc/en-us/requests/new',
      priceDiscrepancyUrl:
        'https://ovagosupport.zendesk.com/hc/en-us/articles/38312904524189'
    },
    isLive: false,
    hideInternalRedirect: true,
    partnerKeys: ['ErkrRskFmk9ULeRYWDnLYtgg'],
    capabilities: {
      ...baseConfig.capabilities,
      commerce: {
        ...baseConfig.capabilities.commerce,
        showVioRoomsOnAp: true,
        bookOnBrand: {
          enabled: true,
          config: {
            helpCenterChatKeyProd: '6c77f974-99e0-47c6-8371-710cc975ce8c',
            helpCenterChatKeyStaging: '6c77f974-99e0-47c6-8371-710cc975ce8c'
          }
        }
      },
      shell: {
        ...baseConfig.capabilities.shell,
        hasDarkMobileHeader: true
      },
      monetisation: {
        ...baseConfig.capabilities.monetisation,
        offersLockedByDefault: false
      },
      tracking: {
        ...baseConfig.capabilities.tracking,
        emails: {
          enable: true,
          cioWorkspace: 'OVAGO',
          defaultLocale: 'en-US'
        }
      },
      compliance: {
        ...baseConfig.capabilities.compliance,
        cookieConsent: {
          enabled: true,
          config: {id: '1ece05db-f1da-4038-8bde-6f02e0808013'}
        }
      }
    }
  },
  karmanow: {
    ...baseConfig,
    code: 'karmanow',
    name: 'Karma',
    siteAddresses: {
      address: 'https://hotels.karmanow.com'
    },
    providerCode: 'karmanow',
    level: 'custom',
    theme: 'karmanow',
    links: {
      privacyPolicyUrl: 'https://www.karmanow.com/privacy',
      termsOfUseUrl: 'https://www.karmanow.com/terms',
      helpUrl: 'https://help.karmanow.com/en',
      contactUrl: 'https://www.karmanow.com/contact'
    },
    isLive: false,
    hideInternalRedirect: false,
    sapiCode: 'karmanowwl',
    partnerKeys: ['HjTLf9iPPJBRS7iUioxXohKF'],
    capabilities: {
      ...baseConfig.capabilities,
      shell: {
        ...baseConfig.capabilities.shell,
        hasDarkMobileHeader: true
      },
      monetisation: {
        ...baseConfig.capabilities.monetisation,
        offersLockedByDefault: false,
        onlyOnBrandTag: {
          enabled: true,
          config: {
            message: defineMessage({
              id: 'vioLabelFinal',
              defaultMessage: 'Only on Vio'
            }),
            filterSectionLabel: defineMessage({
              id: 'vioLabelFinal',
              defaultMessage: 'Only on Vio'
            })
          }
        },
        cashback: {
          enabled: true,
          config: {
            isLongMessageLayout: true,
            alertVariant: 'neutral',
            messages: defineMessages({
              cashbackLongMessage: {
                id: 'wl.kmCashbackMessageFull',
                defaultMessage: '+Earn up to {value} Cash Back'
              },
              cashbackShortMessage: {
                id: 'wl.kmCashbackMessageShort',
                defaultMessage: '+Earn up to {value}'
              },
              cashbackExplanationTitle: {
                id: 'wl.kmCashbackExplanationTitle',
                defaultMessage: 'How <span>Cash Back</span> works'
              },
              cashbackExplanationStep1Title: {
                id: 'cashbackExplanation.step1Title',
                defaultMessage: 'Choose the perfect deal'
              },
              cashbackExplanationStep1Description: {
                id: 'wl.kmCashbackExplanationStep1Description',
                defaultMessage:
                  "You will be redirected to a trusted partner to complete your booking. You'll eligible for Cash Back."
              },
              cashbackExplanationStep2Title: {
                id: 'wl.kmCashbackExplanationStep2Title',
                defaultMessage: 'Earn Cash Back automatically'
              },
              cashbackExplanationStep2Description: {
                id: 'wl.kmCashbackExplanationStep2Description',
                defaultMessage:
                  'When you book, we earn a commission from the provider and share it with you as Cash Back. No codes or extra steps needed.'
              },
              cashbackExplanationStep3Title: {
                id: 'wl.kmCashbackExplanationStep3Title',
                defaultMessage: 'Withdraw your Cash Back'
              },
              cashbackExplanationStep3Description: {
                id: 'wl.kmCashbackExplanationStep3Description',
                defaultMessage:
                  'Your Cash Back will be ready to withdraw within 30 to 60 days of completing your stay.'
              },
              cashbackInfoTitle: {
                id: 'wl.kmCashbackInfoTitle',
                defaultMessage: 'Earn up to {value} Cash Back'
              },
              cashbackInfoContent: {
                id: 'wl.kmCashbackInfoContent',
                defaultMessage:
                  'Your account will be credited after completing your booking. The amount of Cash Back depends on the final booking value. You will be able to see the complete amount of Cash Back in your profile on karmanow.com.'
              }
            })
          }
        }
      },
      compliance: {
        ...baseConfig.capabilities.compliance,
        cookieConsent: {
          enabled: true,
          config: {id: '7ba1e5ba-0dad-41f3-9f4b-b3fa7ef17986'}
        }
      }
    }
  },
  finn: {
    ...baseConfig,
    code: 'finn',
    name: 'FINN.no',
    siteAddresses: {
      address: 'https://hotell.finn.no'
    },
    providerCode: 'finn',
    level: 'custom',
    theme: 'finn',
    isLive: true,
    partnerKeys: ['P5MoXcSxfeNHiHcHPaYCXqjY'],
    hideInternalRedirect: true,
    messages: {
      ...baseConfig.messages,
      ...defineMessages({
        // FINN deliberately suppresses its brand name on sold-out states.
        soldOutLabelMessage: {
          id: 'soldOutTitle',
          defaultMessage: 'Sold out'
        }
      })
    },
    capabilities: {
      ...baseConfig.capabilities,
      commerce: {
        ...baseConfig.capabilities.commerce,
        showVioRoomsOnAp: true,
        coBranded: {
          enabled: true,
          config: {
            messages: defineMessages({
              poweredByTooltipTitle: {
                id: 'wl.poweredByTooltipTitle',
                defaultMessage:
                  'Denne hotelltjenesten leveres av vår partner Vio.com.'
              },
              poweredByTooltipText: {
                id: 'wl.poweredByTooltipText',
                defaultMessage:
                  'FINN er ansvarlig for partnerskapet og samarbeidet, men Vio er behandlingsansvarlig for cookies og personlig informasjon som behandles hos dem.'
              }
            })
          }
        }
      },
      shell: {
        ...baseConfig.capabilities.shell,
        hasAlternativeSearchForm: {
          enabled: true,
          config: {
            hasOutsideInputLabels: true,
            hasTextInputPrefix: false,
            inputsGap: 's300',
            hasTitle: false,
            hasSubtitle: false,
            hasFormTitle: true,
            hasShadow: true,
            heroImage: 'finn-web-hero.png',
            heroVerticalPadding: '143px',
            messages: defineMessages({
              formTitle: {
                id: 'homePage.heading',
                defaultMessage: 'Better value, every stay'
              }
            })
          }
        },
        hasCollapsingHeader: true,
        hasCustomHeader: true,
        mobileSearchHeaderHeight: 172
      },
      monetisation: {
        ...baseConfig.capabilities.monetisation,
        offersLockedByDefault: false
      },
      compliance: {
        ...baseConfig.capabilities.compliance,
        cookieConsent: {
          enabled: true,
          config: {id: '5b00bfa9-a13d-4ee4-8899-808a65632b9f'}
        }
      }
    },
    links: {
      privacyPolicyUrl: 'https://vend.com/privacy/no-policy',
      termsOfUseUrl:
        'https://login.vend.no/about/terms?client_id=5087dc1b421c7a0b79000000&locale=nb_NO'
    }
  },
  hotelix: {
    ...baseConfig,
    code: 'hotelix',
    name: 'Hotelix',
    siteAddresses: {
      address: 'https://hotelix.co.il',
      checkoutAddress: 'https://secure.hotelix.co.il'
    },
    providerCode: 'hotelix',
    messages: {
      ...baseConfig.messages,
      homepageHeadingMessage: defineMessage({
        id: 'wl.hotelixHomePageHeading',
        defaultMessage: 'It’s clear you booked right'
      })
    },
    level: 'custom',
    theme: 'hotelix',
    languageCodes: ['he', 'en'],
    currencyCodes: ['ILS', 'EUR', 'USD'],
    links: {
      helpUrl: 'https://support.hotelix.co.il/',
      privacyPolicyUrl:
        'https://support.hotelix.co.il/hc/en-us/articles/37604019746077-Privacy-Policy',
      cookiePolicyUrl:
        'https://support.hotelix.co.il/hc/en-us/articles/37603732753821-Cookie-Policy',
      termsOfUseUrl:
        'https://support.hotelix.co.il/hc/en-us/articles/37603541528733-Terms-of-Use',
      contactUrl: 'https://support.hotelix.co.il/hc/en-us/requests/new',
      howItWorksUrl:
        'https://support.hotelix.co.il/hc/en-us/articles/37577360489885-Understanding-how-booking-on-Hotelix-works-and-who-manages-your-booking',
      supportEmail: 'support@hotelix.zendesk.com',
      disclosureUrl:
        'https://hotelix.co.il/hc/en-us/il-transactional-disclosure',
      howToCancelUrl:
        'https://support.hotelix.co.il/hc/en-us/articles/37597301395613',
      priceDiscrepancyUrl:
        'https://support.hotelix.co.il/hc/en-us/articles/38313014078237'
    },
    // Placeholders until Hotelix shares its social accounts.
    socials: {
      instagramUrl: 'https://www.instagram.com/',
      facebookUrl: 'https://www.facebook.com/',
      linkedinUrl: 'https://www.linkedin.com/'
    },
    partnerKeys: ['GZ51ceaJvAJlSnr2LvtJMRGk'],
    hideInternalRedirect: true,
    isLive: false,
    capabilities: {
      ...baseConfig.capabilities,
      search: {
        ...baseConfig.capabilities.search,
        quickFilterPresets: {starRatings: ['5']},
        indexesHomepageOnly: true,
        homeTopDealsDestinations: [
          TopDealsDestination.Eilat,
          TopDealsDestination.Jerusalem,
          TopDealsDestination.TelAviv,
          TopDealsDestination.DeadSea,
          TopDealsDestination.Tiberias,
          TopDealsDestination.Athens,
          TopDealsDestination.Dubai,
          TopDealsDestination.Budapest,
          TopDealsDestination.Prague,
          TopDealsDestination.Rome
        ]
      },
      shell: {
        ...baseConfig.capabilities.shell,
        homeHeroLayout: 'marketing',
        hasMarketingHeroGradient: true,
        marketingHeroImage: {
          src: 'hotelix-web-hero.webp',
          width: 830,
          height: 722,
          bleed: {top: 60, inlineEnd: 120}
        },
        hasHomeDesktopSupportButton: true,
        showsHowItWorksLink: true,
        hasFullBrandFooter: true,
        hasCustomFooter: true,
        showSocialMediaLinks: true
      },
      commerce: {
        ...baseConfig.capabilities.commerce,
        showVioRoomsOnAp: true,
        bookOnBrand: {
          enabled: true,
          config: {
            helpCenterChatKeyProd: '39471922-28a7-452f-b552-85beeea7b29c',
            helpCenterChatKeyStaging: '63ab78c1-2a7f-4c4a-a886-cf75248cddc1',
            defaultLanguageCode: 'he',
            defaultCurrencyCode: 'ILS'
          }
        }
      },
      bookings: {
        ...baseConfig.capabilities.bookings,
        hasMyBookings: true
      },
      monetisation: {
        ...baseConfig.capabilities.monetisation,
        offersLockedByDefault: false
      },
      retention: {
        ...baseConfig.capabilities.retention,
        hasPriceWatch: true,
        hasDealFreeze: true,
        hasMyListFraming: true
      },
      tracking: {
        ...baseConfig.capabilities.tracking,
        emails: {
          enable: true,
          cioWorkspace: 'HOTELIX',
          defaultLocale: 'he-IL',
          cioTrackingSiteIds: {
            development: 'e802068283c2cf4ecda8',
            review: 'e802068283c2cf4ecda8',
            staging: 'e802068283c2cf4ecda8',
            // Off, because this brand's other environments all use its production workspace.
            e2e: '',
            production: 'e802068283c2cf4ecda8',
            test: 'e802068283c2cf4ecda8'
          }
        }
      },
      compliance: {
        ...baseConfig.capabilities.compliance,
        hasLegalPages: true,
        cookieConsent: {
          enabled: true,
          config: {id: '3b615ca1-cde8-49cb-915b-d5441ecc30dd'}
        }
      },
      auth: {
        ...baseConfig.capabilities.auth,
        showSignIn: true,
        showUserWidget: true,
        hasEmailOtpSignup: true
      }
    }
  },
  elal: {
    ...baseConfig,
    code: 'elal',
    name: 'El Al',
    siteAddresses: {
      address: 'https://elal.vio.com',
      checkoutAddress: 'https://secure.elal.vio.com'
    },
    providerCode: 'elal',
    messages: {
      ...baseConfig.messages,
      ...defineMessages({
        homepageHeadingMessage: {
          id: 'wl.elalHomePageHeading',
          defaultMessage: 'All Hotels, All Destinations – In One Place!'
        },
        pointsTierMessage: {
          id: 'loyalty.tier',
          defaultMessage: 'Tier Matmid (TL)'
        }
      })
    },
    level: 'custom',
    theme: 'elal',
    links: {
      helpUrl: 'https://elal.zendesk.com/hc/en-us',
      privacyPolicyUrl:
        'https://elal.zendesk.com/hc/en-us/articles/24778216583197-Privacy-Policy',
      termsOfUseUrl:
        'https://elal.zendesk.com/hc/en-us/articles/24778457982621-Terms-of-Use',
      contactUrl: 'https://elal.zendesk.com/hc/en-us/requests/new',
      supportEmail: 'support@elal.zendesk.com'
    },
    partnerKeys: ['GZ51ceaJvAJlSnr2LvtJMRGk'],
    hideInternalRedirect: true,
    isLive: false,
    pointsCouponCode: 'ELAL10',
    capabilities: {
      ...baseConfig.capabilities,
      commerce: {
        ...baseConfig.capabilities.commerce,
        showVioRoomsOnAp: true,
        isOTAMode: true,
        bookOnBrand: {
          enabled: true,
          config: {
            defaultLanguageCode: 'he',
            defaultCurrencyCode: 'ILS'
          }
        }
      },
      bookings: {
        ...baseConfig.capabilities.bookings,
        hasMyBookings: true
      },
      monetisation: {
        ...baseConfig.capabilities.monetisation,
        offersLockedByDefault: false,
        hasPointsProgram: true
      },
      retention: {
        ...baseConfig.capabilities.retention,
        hasPriceWatch: true,
        hasDealFreeze: true,
        hasMyListFraming: true
      },
      tracking: {
        ...baseConfig.capabilities.tracking,
        emails: {
          enable: true,
          cioWorkspace: 'ELAL',
          defaultLocale: 'he-IL',
          cioTrackingSiteIds: {
            development: 'a9c722d97be1108dfb43',
            review: 'a9c722d97be1108dfb43',
            staging: 'a9c722d97be1108dfb43',
            // Off, because this brand's other environments all use its production workspace.
            e2e: '',
            production: 'a9c722d97be1108dfb43',
            test: 'a9c722d97be1108dfb43'
          }
        }
      },
      compliance: {
        ...baseConfig.capabilities.compliance,
        cookieConsent: {
          enabled: true,
          config: {id: '45cb8e0c-8627-4f92-9747-0c60c4aa7bf8'}
        }
      },
      auth: {
        ...baseConfig.capabilities.auth,
        showSignIn: true,
        showUserWidget: true,
        requiresSignInForCheckout: true
      }
    }
  },
  novaair: {
    ...baseConfig,
    code: 'novaair',
    name: 'NovaAir',
    siteAddresses: {
      address: 'https://novaair.vio.com',
      checkoutAddress: 'https://secure.novaair.vio.com'
    },
    providerCode: 'novaair',
    messages: {
      ...baseConfig.messages,
      ...defineMessages({
        homepageHeadingMessage: {
          id: 'wl.novaairHomePageHeading',
          defaultMessage: 'Elevate your hotel experience'
        },
        pointsTierMessage: {
          id: 'wl.novaairPointsTier',
          defaultMessage: 'Tier Gold'
        }
      })
    },
    level: 'custom',
    theme: 'novaair',
    links: {
      helpUrl: 'https://support.vio.com/',
      privacyPolicyUrl:
        'https://support.vio.com/hc/en-us/articles/37314221746461-Privacy-Policy',
      termsOfUseUrl:
        'https://support.vio.com/hc/en-us/articles/37509195342109-Terms-of-Use',
      contactUrl:
        'https://support.vio.com/hc/en-us/requests/new?ticket_form_id=6796130090397',
      supportEmail: 'support@vio.com'
    },
    partnerKeys: ['GZ51ceaJvAJlSnr2LvtJMRGk'],
    hideInternalRedirect: true,
    isLive: false,
    pointsCouponCode: 'NOVA10',
    capabilities: {
      ...baseConfig.capabilities,
      commerce: {
        ...baseConfig.capabilities.commerce,
        showVioRoomsOnAp: true,
        isOTAMode: true,
        bookOnBrand: {
          enabled: true,
          config: {
            defaultLanguageCode: 'en',
            defaultCurrencyCode: 'EUR'
          }
        }
      },
      bookings: {
        ...baseConfig.capabilities.bookings,
        hasMyBookings: true
      },
      monetisation: {
        ...baseConfig.capabilities.monetisation,
        offersLockedByDefault: false,
        hasPointsProgram: true,
        hasEarnedPoints: true
      },
      compliance: {
        ...baseConfig.capabilities.compliance,
        cookieConsent: {
          enabled: true,
          config: {id: '5948ea37-2a34-4959-96dc-b6a98a9c8605'}
        }
      },
      shell: {
        ...baseConfig.capabilities.shell,
        hasHomeComingSoonSection: true,
        hasAlternativeSearchForm: {
          enabled: true,
          config: {
            hasTitle: true,
            hasSubtitle: false,
            hasFormTitle: false,
            hasOutsideInputLabels: false,
            hasTextInputPrefix: false,
            inputsGap: 's300',
            hasShadow: false,
            heroImage: 'novaair-web-hero.jpg',
            hasMobileHeroImage: true,
            isHomepageOnly: true,
            heroVerticalPadding: '70px'
          }
        }
      },
      auth: {
        ...baseConfig.capabilities.auth,
        showSignIn: true,
        showUserWidget: true
      },
      retention: {
        ...baseConfig.capabilities.retention,
        hasPriceWatch: true,
        hasDealFreeze: true,
        hasMyListFraming: true
      },
      tracking: {
        ...baseConfig.capabilities.tracking,
        emails: {
          enable: true,
          cioWorkspace: 'NOVAAIR',
          defaultLocale: 'en-US',
          cioTrackingSiteIds: {
            development: '2e886d489fb43f1eb0fe',
            review: '2e886d489fb43f1eb0fe',
            staging: '2e886d489fb43f1eb0fe',
            // Off, because this brand's other environments all use its production workspace.
            e2e: '',
            production: '2e886d489fb43f1eb0fe',
            test: '2e886d489fb43f1eb0fe'
          }
        }
      }
    }
  }
}
